import * as React from "react";
import { useNotify } from "react-admin";
import {
  Box, Card, CardContent, Typography, TextField, Button, Stack,
  CircularProgress, Alert, Table, TableHead, TableBody, TableRow, TableCell,
} from "@mui/material";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { httpClient } from "../../api/httpClient";
import { API_BASE_URL } from "../../api/config";

// SlotDTO.startTime / endTime : epoch ms
const fmtTs = (ts) => (ts != null ? new Date(Number(ts)).toLocaleString("fr-FR") : "—");

const AbsencesList = () => {
  const [proId, setProId] = React.useState("");
  const [absences, setAbsences] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [start, setStart] = React.useState("");
  const [end, setEnd] = React.useState("");
  const notify = useNotify();

  const fetch = async () => {
    if (!proId.trim()) return;
    setLoading(true);
    try {
      const { json } = await httpClient(
        `${API_BASE_URL}/calendars/users/${encodeURIComponent(proId)}/absences`
      );
      setAbsences(Array.isArray(json) ? json : json?.data || []);
    } catch (e) {
      notify(`Erreur : ${e.message}`, { type: "error" });
      setAbsences([]);
    } finally {
      setLoading(false);
    }
  };

  const create = async () => {
    if (!proId.trim() || !start || !end) return;
    try {
      await httpClient(`${API_BASE_URL}/calendars/users/${encodeURIComponent(proId)}/absences`, {
        method: "POST",
        // AbsenceRequestDTO : timestamps en millisecondes (epoch)
        body: JSON.stringify({
          startTimestamp: new Date(start).getTime(),
          endTimestamp: new Date(end).getTime(),
          type: "slots",
        }),
      });
      notify("Absence créée", { type: "success" });
      setStart(""); setEnd("");
      fetch();
    } catch (e) {
      notify(`Erreur : ${e.message}`, { type: "error" });
    }
  };

  // DELETE attend startTimestamp & endTimestamp (ms) en query params
  // (CalendarController.deleteAbsence) : on reprend les bornes du slot.
  const remove = async (slot) => {
    const qs = new URLSearchParams({
      startTimestamp: String(slot.startTime),
      endTimestamp: String(slot.endTime),
    });
    try {
      await httpClient(
        `${API_BASE_URL}/calendars/users/${encodeURIComponent(proId)}/absences?${qs}`,
        { method: "DELETE" }
      );
      notify("Absence supprimée", { type: "success" });
      fetch();
    } catch (e) {
      notify(`Erreur : ${e.message}`, { type: "error" });
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <EventBusyIcon /> Absences
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Gestion des absences d'un professionnel.
      </Alert>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack spacing={2}>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <TextField size="small" label="ID Pro"
                value={proId} onChange={(e) => setProId(e.target.value)} />
              <Button variant="contained" onClick={fetch} disabled={loading || !proId.trim()}>
                {loading ? <CircularProgress size={18} /> : "Lister"}
              </Button>
            </Box>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <TextField size="small" label="Début" type="datetime-local"
                value={start} onChange={(e) => setStart(e.target.value)}
                InputLabelProps={{ shrink: true }} />
              <TextField size="small" label="Fin" type="datetime-local"
                value={end} onChange={(e) => setEnd(e.target.value)}
                InputLabelProps={{ shrink: true }} />
              <Button variant="outlined" startIcon={<AddIcon />} onClick={create}
                disabled={!proId.trim() || !start || !end}>
                Créer absence
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Absences existantes</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Début</TableCell>
                <TableCell>Fin</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {absences.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">Aucune absence</TableCell>
                </TableRow>
              ) : absences.map((a, i) => (
                <TableRow key={a.id || i}>
                  <TableCell>{a.id || "—"}</TableCell>
                  <TableCell>{fmtTs(a.startTime)}</TableCell>
                  <TableCell>{fmtTs(a.endTime)}</TableCell>
                  <TableCell>
                    <Button size="small" color="error" startIcon={<DeleteIcon />}
                      onClick={() => remove(a)}>
                      Supprimer
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </Box>
  );
};

export default { list: AbsencesList };
